/**
 * src/pages/Market.tsx
 *
 * Market intelligence tab — Tony's 2026-05-10 ask for the prospective
 * market intel analyst. Pure read-layer over ebay_sold_listings + deals
 * (cex_uk / ebay_uk). Multiple complementary views:
 *
 *   A. Headline KPIs
 *   B. Per-modelKey trend (eBay sold P25/P50/P75 + CeX cash/voucher)
 *   C. Supply signal panel (sold velocity + live listings side-by-side)
 *   D. Top movers (WoW % change)
 *   E. Price distribution (histogram for selected modelKey)
 *   F. Spread vs CeX table
 *
 * Filter row at top: modelKey picker + time-window toggle. Both control
 * sections B / C / E. Sections A / D / F use the time-window only.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  getMarketHeadline,
  getMarketSeries,
  getMarketSupply,
  getMarketMovers,
  getMarketDistribution,
  getMarketSpread,
  marketExportPath,
  type SeriesBucket,
  type SupplyBucket,
  type Mover,
  type SpreadRow,
} from '@/api/market';
import ChartCard from '@/components/ChartCard';
import TimeWindowToggle from '@/components/TimeWindowToggle';
import ModelKeyPicker from '@/components/ModelKeyPicker';
import Histogram from '@/components/Histogram';
import CsvExportButton from '@/components/CsvExportButton';
import { cn } from '@/lib/cn';
import { txt } from '@/lib/format';

const DAY = 24 * 60 * 60 * 1000;

export default function Market() {
  const [sinceMs, setSinceMs]   = useState<number>(30 * DAY);
  const [modelKey, setModelKey] = useState<string>('');

  return (
    <div className="space-y-4 max-w-7xl">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Market</h1>
          <p className="text-sm text-slate-500 mt-1">
            Price trend + supply signal by modelKey · eBay sold + CeX cash overlay
          </p>
        </div>
        <TimeWindowToggle value={sinceMs} onChange={setSinceMs} label="window" />
      </header>

      <HeadlineRow sinceMs={sinceMs} />

      <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-4">
        <div>
          <ModelKeyPicker value={modelKey} onChange={setModelKey} popularSinceMs={sinceMs} />
        </div>
        <div className="space-y-4 min-w-0">
          <TrendCard modelKey={modelKey} sinceMs={sinceMs} />
          <SupplyCard modelKey={modelKey} sinceMs={sinceMs} />
          <DistributionCard modelKey={modelKey} sinceMs={sinceMs} />
        </div>
      </div>

      <MoversCard sinceMs={sinceMs} onPick={setModelKey} />
      <SpreadCard sinceMs={sinceMs} onPick={setModelKey} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A. Headline KPI row
// ─────────────────────────────────────────────────────────────────────────────

function HeadlineRow({ sinceMs }: { sinceMs: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['market', 'headline', sinceMs],
    queryFn:  () => getMarketHeadline({ sinceMs }),
  });

  const Kpi = ({ label, value }: { label: string; value: string | number | null }) => (
    <div className="rounded border border-slate-800 bg-slate-900/30 px-3 py-2 min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-base font-mono mt-0.5 truncate">
        {isLoading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (value ?? '—')}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <Kpi label="sold listings" value={data?.soldListings?.toLocaleString() ?? null} />
      <Kpi label="distinct models" value={data?.distinctModelKeys?.toLocaleString() ?? null} />
      <Kpi label="cex with anchor" value={data?.cexListingsWithAnchor?.toLocaleString() ?? null} />
      <Kpi label="top traded"
           value={data?.topTradedModelKey
             ? `${data.topTradedModelKey.modelKey} (${data.topTradedModelKey.soldCount})`
             : null} />
      <Kpi label="median ebay-vs-cex %"
           value={data?.medianSoldVsCexSpreadPct !== null && data?.medianSoldVsCexSpreadPct !== undefined
             ? `${data.medianSoldVsCexSpreadPct > 0 ? '+' : ''}${data.medianSoldVsCexSpreadPct}%`
             : null} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// B. Per-modelKey trend
// ─────────────────────────────────────────────────────────────────────────────

function TrendCard({ modelKey, sinceMs }: { modelKey: string; sinceMs: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['market', 'series', modelKey, sinceMs],
    queryFn:  () => getMarketSeries({ modelKey, sinceMs }),
    enabled:  !!modelKey,
  });

  if (!modelKey) {
    return (
      <ChartCard title="Price trend" subtitle="Pick a modelKey to see eBay sold + CeX cash trends">
        <div className="text-xs text-slate-500 py-4">No modelKey selected.</div>
      </ChartCard>
    );
  }

  const isEmpty = !!data && data.buckets.length === 0;

  return (
    <ChartCard
      title={`Price trend — ${modelKey}`}
      subtitle="eBay sold P25/P50/P75 band · CeX cash + voucher overlay"
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      emptyLabel="No price history for this modelKey in the selected window."
      actions={
        <CsvExportButton path={marketExportPath({ modelKey, sinceMs })} filename={`market_${modelKey}.csv`} />
      }
    >
      {data && <PriceTrendChart buckets={data.buckets} />}
    </ChartCard>
  );
}

function PriceTrendChart({ buckets }: { buckets: SeriesBucket[] }) {
  if (!buckets.length) return null;
  const allValues: number[] = [];
  for (const b of buckets) {
    if (b.ebayP25 !== undefined) allValues.push(b.ebayP25);
    if (b.ebayP75 !== undefined) allValues.push(b.ebayP75);
    if (b.ebayP50 !== undefined) allValues.push(b.ebayP50);
    if (b.cexCash !== undefined) allValues.push(b.cexCash);
    if (b.cexVoucher !== undefined) allValues.push(b.cexVoucher);
  }
  const max = Math.max(1, ...allValues);
  const min = Math.min(...allValues);
  const range = max - min || 1;
  const w = 100;
  const h = 200;
  const padY = 10;
  const stepX = w / Math.max(1, buckets.length - 1);

  const project = (v: number) => (h - padY) - ((v - min) / range) * (h - padY * 2);

  const ebayBand: string[] = [];
  const ebayMedPts: string[] = [];
  const cexCashPts: string[] = [];
  const cexVoucherPts: string[] = [];

  // Build the P25–P75 polygon (forward then backward)
  const forwardP25: string[] = [];
  const backwardP75: string[] = [];
  buckets.forEach((b, i) => {
    const x = i * stepX;
    if (b.ebayP25 !== undefined) forwardP25.push(`${x.toFixed(2)},${project(b.ebayP25).toFixed(2)}`);
    if (b.ebayP50 !== undefined) ebayMedPts.push(`${x.toFixed(2)},${project(b.ebayP50).toFixed(2)}`);
    if (b.cexCash !== undefined) cexCashPts.push(`${x.toFixed(2)},${project(b.cexCash).toFixed(2)}`);
    if (b.cexVoucher !== undefined) cexVoucherPts.push(`${x.toFixed(2)},${project(b.cexVoucher).toFixed(2)}`);
  });
  for (let i = buckets.length - 1; i >= 0; i--) {
    const b = buckets[i];
    const x = i * stepX;
    if (b.ebayP75 !== undefined) backwardP75.push(`${x.toFixed(2)},${project(b.ebayP75).toFixed(2)}`);
  }
  if (forwardP25.length && backwardP75.length) {
    ebayBand.push('M', forwardP25.join(' L '), 'L', backwardP75.join(' L '), 'Z');
  }

  return (
    <div className="space-y-2">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
        {ebayBand.length > 0 && <path d={ebayBand.join(' ')} fill="#3b82f6" fillOpacity={0.12} />}
        {ebayMedPts.length > 0 && (
          <polyline points={ebayMedPts.join(' ')} fill="none" stroke="#3b82f6" strokeWidth={1.2} />
        )}
        {cexCashPts.length > 0 && (
          <polyline points={cexCashPts.join(' ')} fill="none" stroke="#34d399" strokeWidth={1} />
        )}
        {cexVoucherPts.length > 0 && (
          <polyline points={cexVoucherPts.join(' ')} fill="none" stroke="#34d399" strokeWidth={0.8} strokeDasharray="2 2" />
        )}
      </svg>
      <div className="flex gap-3 text-[11px] text-slate-400 flex-wrap">
        <Legend color="#3b82f6" label="eBay sold P50" />
        <Legend color="#3b82f6" label="eBay P25–P75 band" opacity={0.3} />
        <Legend color="#34d399" label="CeX cash" />
        <Legend color="#34d399" label="CeX voucher" dashed />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 font-mono">
        <span>{buckets[0]?.ts.slice(0, 10)}</span>
        <span>{buckets[buckets.length - 1]?.ts.slice(0, 10)}</span>
      </div>
    </div>
  );
}

function Legend({ color, label, dashed, opacity }: { color: string; label: string; dashed?: boolean; opacity?: number }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block w-3 h-1 align-middle"
        style={{ background: color, opacity, borderStyle: dashed ? 'dashed' : undefined }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// C. Supply signal — sold velocity + live listings side by side
// ─────────────────────────────────────────────────────────────────────────────

function SupplyCard({ modelKey, sinceMs }: { modelKey: string; sinceMs: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['market', 'supply', modelKey, sinceMs],
    queryFn:  () => getMarketSupply({ modelKey, sinceMs }),
    enabled:  !!modelKey,
  });
  if (!modelKey) return null;
  const isEmpty = !!data && data.buckets.length === 0;
  return (
    <ChartCard
      title="Supply signal"
      subtitle="eBay sold velocity (demand) · live listings count (inventory)"
      isLoading={isLoading} isError={isError} isEmpty={isEmpty}
    >
      {data && <SupplyChart buckets={data.buckets} />}
    </ChartCard>
  );
}

function SupplyChart({ buckets }: { buckets: SupplyBucket[] }) {
  if (!buckets.length) return null;
  const maxSold = Math.max(1, ...buckets.map(b => b.soldVelocity));
  const maxLive = Math.max(1, ...buckets.map(b => b.liveListings));
  const w = 100, h = 90;
  const stepX = w / Math.max(1, buckets.length - 1);

  const bars = (max: number, accessor: (b: SupplyBucket) => number, color: string) => buckets.map((b, i) => {
    const v = accessor(b);
    const barH = (v / max) * (h - 4);
    const x = i * stepX;
    return <rect key={i} x={x - stepX * 0.4} y={h - barH - 1} width={stepX * 0.8} height={barH} fill={color} />;
  });

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-[11px] text-slate-500 mb-1">sold velocity · max {maxSold}</div>
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
          {bars(maxSold, b => b.soldVelocity, '#fb7185')}
        </svg>
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">live listings · max {maxLive}</div>
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height: h }}>
          {bars(maxLive, b => b.liveListings, '#34d399')}
        </svg>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// E. Price distribution
// ─────────────────────────────────────────────────────────────────────────────

function DistributionCard({ modelKey, sinceMs }: { modelKey: string; sinceMs: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['market', 'distribution', modelKey, sinceMs],
    queryFn:  () => getMarketDistribution({ modelKey, sinceMs, binCount: 20 }),
    enabled:  !!modelKey,
  });
  if (!modelKey) return null;
  const isEmpty = !!data && data.bins.length === 0;
  return (
    <ChartCard
      title="Price distribution"
      subtitle="Histogram of eBay sold prices in the window — spot bimodal / skewed distributions"
      isLoading={isLoading} isError={isError} isEmpty={isEmpty}
    >
      {data && <Histogram bins={data.bins} color="#a855f7" />}
      {data && <div className="text-[11px] text-slate-500 mt-2">{data.total.toLocaleString()} listings</div>}
    </ChartCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// D. Top movers
// ─────────────────────────────────────────────────────────────────────────────

function MoversCard({ sinceMs, onPick }: { sinceMs: number; onPick: (mk: string) => void }) {
  const [sort, setSort] = useState<'up' | 'down' | 'abs' | 'volume'>('abs');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['market', 'movers', sinceMs, sort],
    queryFn:  () => getMarketMovers({ sinceMs, sort, limit: 30 }),
  });
  const rows = data?.rows ?? [];
  return (
    <ChartCard
      title="Top movers"
      subtitle="modelKeys with the biggest median price change in the window (split at mid-point)"
      isLoading={isLoading} isError={isError} isEmpty={rows.length === 0}
      actions={
        <select
          value={sort}
          onChange={e => setSort(e.target.value as any)}
          className="bg-slate-900 border border-slate-800 rounded text-xs px-1.5 py-0.5"
        >
          <option value="abs">biggest move</option>
          <option value="up">biggest rise</option>
          <option value="down">biggest drop</option>
          <option value="volume">biggest volume</option>
        </select>
      }
    >
      <MoversTable rows={rows} onPick={onPick} />
    </ChartCard>
  );
}

function MoversTable({ rows, onPick }: { rows: Mover[]; onPick: (mk: string) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-slate-500">
          <tr>
            <th className="text-left  px-2 py-1.5">modelKey</th>
            <th className="text-right px-2 py-1.5">prior</th>
            <th className="text-right px-2 py-1.5">current</th>
            <th className="text-right px-2 py-1.5">Δ</th>
            <th className="text-right px-2 py-1.5">Δ%</th>
            <th className="text-right px-2 py-1.5">vol</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.modelKey}
              onClick={() => onPick(r.modelKey)}
              className="border-t border-slate-800 hover:bg-slate-900/40 cursor-pointer"
            >
              <td className="px-2 py-1.5 font-mono text-slate-200">{txt(r.modelKey)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">£{r.priorMedian}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">£{r.currentMedian}</td>
              <td className={cn(
                'px-2 py-1.5 text-right tabular-nums',
                r.deltaAbs > 0 ? 'text-emerald-400' : 'text-rose-400',
              )}>
                {r.deltaAbs > 0 ? '+' : ''}£{r.deltaAbs}
              </td>
              <td className={cn(
                'px-2 py-1.5 text-right tabular-nums font-mono',
                r.deltaPct > 0 ? 'text-emerald-400' : 'text-rose-400',
              )}>
                {r.deltaPct > 0 ? '+' : ''}{r.deltaPct}%
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{r.currentVolume}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// F. Spread vs CeX
// ─────────────────────────────────────────────────────────────────────────────

function SpreadCard({ sinceMs, onPick }: { sinceMs: number; onPick: (mk: string) => void }) {
  const [sort, setSort] = useState<'positive' | 'negative' | 'absolute' | 'volume'>('positive');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['market', 'spread', sinceMs, sort],
    queryFn:  () => getMarketSpread({ sinceMs, sort, limit: 30 }),
  });
  const rows = data?.rows ?? [];
  return (
    <ChartCard
      title="Spread vs CeX"
      subtitle="eBay sold median vs CeX cash — positive = eBay buyers pay more than CeX cash (arbitrage signal)"
      isLoading={isLoading} isError={isError} isEmpty={rows.length === 0}
      actions={
        <select
          value={sort}
          onChange={e => setSort(e.target.value as any)}
          className="bg-slate-900 border border-slate-800 rounded text-xs px-1.5 py-0.5"
        >
          <option value="positive">biggest +spread</option>
          <option value="negative">biggest −spread</option>
          <option value="absolute">biggest abs</option>
          <option value="volume">biggest volume</option>
        </select>
      }
    >
      <SpreadTable rows={rows} onPick={onPick} />
    </ChartCard>
  );
}

function SpreadTable({ rows, onPick }: { rows: SpreadRow[]; onPick: (mk: string) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-slate-500">
          <tr>
            <th className="text-left  px-2 py-1.5">modelKey</th>
            <th className="text-right px-2 py-1.5">eBay £</th>
            <th className="text-right px-2 py-1.5">CeX cash</th>
            <th className="text-right px-2 py-1.5">spread</th>
            <th className="text-right px-2 py-1.5">spread %</th>
            <th className="text-right px-2 py-1.5">vol</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.modelKey}
              onClick={() => onPick(r.modelKey)}
              className="border-t border-slate-800 hover:bg-slate-900/40 cursor-pointer"
            >
              <td className="px-2 py-1.5 font-mono text-slate-200">{txt(r.modelKey)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">£{r.ebayMedian}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">£{r.cexCash}</td>
              <td className={cn(
                'px-2 py-1.5 text-right tabular-nums',
                r.spreadAbs > 0 ? 'text-emerald-400' : 'text-rose-400',
              )}>
                {r.spreadAbs > 0 ? '+' : ''}£{r.spreadAbs}
              </td>
              <td className={cn(
                'px-2 py-1.5 text-right tabular-nums font-mono',
                r.spreadPct > 0 ? 'text-emerald-400' : 'text-rose-400',
              )}>
                {r.spreadPct > 0 ? '+' : ''}{r.spreadPct}%
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{r.ebayVolume}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
